import {
  beginAttioConnect,
  beginSalesforceConnect,
  getAttioStatus,
  getCalendarStatus,
  getGmailStatus,
  getSalesforceStatus,
  getSlackConnectionStatus,
  isGmailReadyForPerformer,
} from "../businessModeService";
import {
  BUSINESS_OAUTH_POPUP_RESULT_KEY,
  stripPopupOauthState,
  subscribeBusinessOAuthPopupResult,
  toPopupOauthState,
} from "./shared/businessDigestSetup";

/** OAuth state value for Attio CRM connect from AI Assist. */
export const PERFORMER_ATTIO_OAUTH_STATE = "business_attio";

/** OAuth state value for Salesforce CRM connect from AI Assist. */
export const PERFORMER_SALESFORCE_OAUTH_STATE = "business_salesforce";

/** Storage key: pathname+search to return to after Gmail OAuth for the action-item performer. */
export const PERFORMER_GMAIL_OAUTH_RETURN_KEY = "performerGmailOAuthReturn";

/**
 * Read stored post-OAuth return path (localStorage works across OAuth popup tabs).
 *
 * @example
 * getPerformerGmailOAuthReturnUrl();
 * // "/business/settings?settingsTab=general"
 */
export function getPerformerGmailOAuthReturnUrl() {
  if (typeof window === "undefined") {
    return "";
  }
  return (
    localStorage.getItem(PERFORMER_GMAIL_OAUTH_RETURN_KEY) ||
    sessionStorage.getItem(PERFORMER_GMAIL_OAUTH_RETURN_KEY) ||
    ""
  );
}

/**
 * Clear stored post-OAuth return path after a successful callback exchange.
 *
 * @example
 * clearPerformerGmailOAuthReturnUrl();
 */
export function clearPerformerGmailOAuthReturnUrl() {
  if (typeof window === "undefined") {
    return;
  }
  localStorage.removeItem(PERFORMER_GMAIL_OAUTH_RETURN_KEY);
  sessionStorage.removeItem(PERFORMER_GMAIL_OAUTH_RETURN_KEY);
}

const GMAIL_POLL_INTERVAL_MS = 2000;
const GMAIL_POLL_TIMEOUT_MS = 10 * 60 * 1000;

/** Dispatched on `window` after `/externalauth/gmail/callback` succeeds for performer Gmail OAuth. */
export const PERFORMER_GMAIL_OAUTH_CONNECTED_EVENT = "starmesh-performer-gmail-connected";

/** OAuth state value for performer Drive connect from General settings. */
export const PERFORMER_DRIVE_OAUTH_STATE = "performer_drive";

/** Dispatched after Drive OAuth succeeds for performer context. */
export const PERFORMER_DRIVE_OAUTH_CONNECTED_EVENT = "starmesh-performer-drive-connected";

/** OAuth state value for performer Calendar connect from General settings. */
export const PERFORMER_CALENDAR_OAUTH_STATE = "performer_calendar";

/** Dispatched after Calendar OAuth succeeds for FreeBusy suggestions. */
export const PERFORMER_CALENDAR_OAUTH_CONNECTED_EVENT =
  "starmesh-performer-calendar-connected";

/** OAuth state value for performer Slack connect from General settings. */
export const PERFORMER_SLACK_OAUTH_STATE = "performer_slack";

/** Dispatched after Slack OAuth succeeds for the action-item performer. */
export const PERFORMER_SLACK_OAUTH_CONNECTED_EVENT = "starmesh-performer-slack-connected";

/**
 * Remember the current URL so post-OAuth redirect can send the user back here.
 *
 * @example
 * // User is on /business/action-items (or a workflow settings URL)
 * setPerformerGmailOAuthReturnUrl();
 * // sessionStorage now holds the current pathname + search
 */
export function setPerformerGmailOAuthReturnUrl() {
  if (typeof window === "undefined") {
    return;
  }
  const returnPath = window.location.pathname + window.location.search;
  localStorage.setItem(PERFORMER_GMAIL_OAUTH_RETURN_KEY, returnPath);
  sessionStorage.setItem(PERFORMER_GMAIL_OAUTH_RETURN_KEY, returnPath);
}

/** Business webapp route for unified action-items settings (full page). */
const ACTION_ITEMS_SETTINGS_PATH = "/business/settings";

/**
 * Resolve where a performer Gmail/Drive OAuth redirect (landing on `/` with
 * `code` + `state=performer_gmail`/`performer_drive`) should be sent so
 * `usePerformerOAuthCallback` can complete the token exchange.
 * Returns null when `search` doesn't carry a performer OAuth code.
 *
 * @example
 * // search: "?code=abc&state=performer_gmail"
 * getPerformerOAuthRedirectPath(search);
 * // "/business/settings?settingsTab=general&code=abc&state=performer_gmail"
 */
export function getPerformerOAuthRedirectPath(search) {
  const params = new URLSearchParams(search || "");
  const code = params.get("code");
  const state = stripPopupOauthState(params.get("state"));
  if (
    !code ||
    (state !== "performer_gmail" &&
      state !== PERFORMER_DRIVE_OAUTH_STATE &&
      state !== PERFORMER_CALENDAR_OAUTH_STATE &&
      state !== PERFORMER_SLACK_OAUTH_STATE)
  ) {
    return null;
  }

  const stored = getPerformerGmailOAuthReturnUrl();
  const targetPath =
    stored && stored.startsWith("/business/") ? stored : ACTION_ITEMS_SETTINGS_PATH;
  const returnUrl = new URL(targetPath, window.location.origin);
  if (stored && stored.startsWith("/business/")) {
    const storedUrl = new URL(stored, window.location.origin);
    storedUrl.searchParams.forEach((value, key) => {
      if (!["code", "state", "scope"].includes(key)) {
        returnUrl.searchParams.set(key, value);
      }
    });
  }
  for (const key of ["code", "state", "scope"]) {
    const value = params.get(key);
    if (value) {
      returnUrl.searchParams.set(key, value);
    }
  }
  return `${returnUrl.pathname}${returnUrl.search}`;
}

/**
 * Open Gmail OAuth in a new tab and poll until performer Gmail access is verified.
 *
 * @example
 * // Input: "https://accounts.google.com/o/oauth2/v2/auth?client_id=..."
 * const stop = startPerformerGmailOAuthFlow(authUrl, () => retryDraftRun());
 * // Output: OAuth opens in a new tab; when verify passes, onReady runs once.
 *
 * @example
 * // Input: stop()
 * // Output: polling stops and onReady will not run again
 */
export function startPerformerGmailOAuthFlow(gmailAuthUrl, onReady, onPollEnd) {
  if (!gmailAuthUrl || typeof window === "undefined") {
    return () => {};
  }

  setPerformerGmailOAuthReturnUrl();
  const authUrl = new URL(gmailAuthUrl);
  authUrl.searchParams.set("state", toPopupOauthState("performer_gmail"));
  window.open(authUrl.toString(), "_blank", "noopener");

  let stopped = false;
  let intervalId = null;
  const stop = (succeeded = false) => {
    if (stopped) return;
    stopped = true;
    if (intervalId) {
      clearInterval(intervalId);
      intervalId = null;
    }
    document.removeEventListener("visibilitychange", handleVisibility);
    if (!succeeded) {
      onPollEnd?.();
    }
  };

  const poll = async () => {
    if (stopped) return;
    try {
      const status = await getGmailStatus({ verify: "performer" });
      if (stopped) return;
      if (isGmailReadyForPerformer(status)) {
        stop(true);
        onReady?.(status);
      }
    } catch {
      // keep polling until timeout
    }
  };
  // The OAuth tab is what's focused during consent, so this tab is
  // backgrounded and its setInterval gets throttled by the browser -- catch
  // up immediately once the user switches back instead of waiting on the
  // next (possibly much-delayed) throttled tick.
  const handleVisibility = () => {
    if (document.visibilityState === "visible") void poll();
  };

  void poll();
  intervalId = setInterval(poll, GMAIL_POLL_INTERVAL_MS);
  document.addEventListener("visibilitychange", handleVisibility);
  setTimeout(() => stop(false), GMAIL_POLL_TIMEOUT_MS);
  return () => stop(false);
}

/**
 * Open Drive OAuth in a new tab and poll until Drive access is stored.
 *
 * @example
 * // Input: "https://accounts.google.com/o/oauth2/v2/auth?...&purpose=drive"
 * const stop = startPerformerDriveOAuthFlow(authUrl, () => setDriveReady(true));
 * // Output: new tab opens; onReady runs once drive_authorized is true
 */
export function startPerformerDriveOAuthFlow(driveAuthUrl, onReady) {
  if (!driveAuthUrl || typeof window === "undefined") {
    return () => {};
  }

  setPerformerGmailOAuthReturnUrl();
  const authUrl = new URL(driveAuthUrl);
  authUrl.searchParams.set("state", toPopupOauthState(PERFORMER_DRIVE_OAUTH_STATE));
  window.open(authUrl.toString(), "_blank", "noopener");

  let stopped = false;
  let intervalId = null;
  const stop = () => {
    stopped = true;
    if (intervalId) {
      clearInterval(intervalId);
      intervalId = null;
    }
    document.removeEventListener("visibilitychange", handleVisibility);
  };

  const poll = async () => {
    if (stopped) return;
    try {
      const status = await getGmailStatus({ verify: "performer" });
      if (stopped) return;
      if (status.drive_authorized === true) {
        stop();
        onReady?.(status);
      }
    } catch {
      // keep polling until timeout
    }
  };
  const handleVisibility = () => {
    if (document.visibilityState === "visible") void poll();
  };

  void poll();
  intervalId = setInterval(poll, GMAIL_POLL_INTERVAL_MS);
  document.addEventListener("visibilitychange", handleVisibility);
  setTimeout(stop, GMAIL_POLL_TIMEOUT_MS);
  return stop;
}

/**
 * Open Slack OAuth in a new tab and poll until a send-capable credential exists.
 *
 * Slack's `auth_url` comes back from the backend with a default `state=business_slack`
 * baked in — the same state the legacy (now unlinked) Slack-digest workflow wizard
 * uses, which a global redirect in App.js maps straight to that wizard's route. Left
 * as-is, completing OAuth here would hijack the popup tab into that unrelated wizard
 * instead of back to General settings. So, same as Gmail/Drive/Calendar, the `state`
 * query param is overwritten client-side before opening the tab — Slack only ever
 * echoes `state` back verbatim (see `SlackHandler.get_auth_url`), so swapping it here
 * is safe and doesn't affect the OAuth exchange itself.
 *
 * @example
 * const stop = startPerformerSlackOAuthFlow(authUrl, () => setSlackReady(true));
 */
export function startPerformerSlackOAuthFlow(slackAuthUrl, onReady, onPollEnd) {
  if (!slackAuthUrl || typeof window === "undefined") {
    return () => {};
  }

  setPerformerGmailOAuthReturnUrl();
  const authUrl = new URL(slackAuthUrl);
  authUrl.searchParams.set("state", toPopupOauthState(PERFORMER_SLACK_OAUTH_STATE));
  window.open(authUrl.toString(), "_blank", "noopener,noreferrer");

  let stopped = false;
  let intervalId = null;
  const stop = (succeeded = false) => {
    if (stopped) return;
    stopped = true;
    if (intervalId) {
      clearInterval(intervalId);
      intervalId = null;
    }
    document.removeEventListener("visibilitychange", handleVisibility);
    if (!succeeded) {
      onPollEnd?.();
    }
  };

  const poll = async () => {
    if (stopped) return;
    try {
      const status = await getSlackConnectionStatus();
      if (stopped) return;
      if (status?.connected === true && status?.has_send_scope === true) {
        stop(true);
        onReady?.(status);
      }
    } catch {
      // keep polling until timeout
    }
  };
  // The OAuth tab is what's focused during consent, so this tab is
  // backgrounded and its setInterval gets throttled by the browser -- catch
  // up immediately once the user switches back instead of waiting on the
  // next (possibly much-delayed) throttled tick.
  const handleVisibility = () => {
    if (document.visibilityState === "visible") void poll();
  };

  void poll();
  intervalId = setInterval(poll, GMAIL_POLL_INTERVAL_MS);
  document.addEventListener("visibilitychange", handleVisibility);
  setTimeout(() => stop(false), GMAIL_POLL_TIMEOUT_MS);
  return () => stop(false);
}

/**
 * Open Google Calendar OAuth and poll until a Calendar credential exists.
 *
 * @example
 * const stop = startPerformerCalendarOAuthFlow(authUrl, () => setCalendarReady(true));
 */
export function startPerformerCalendarOAuthFlow(calendarAuthUrl, onReady) {
  if (!calendarAuthUrl || typeof window === "undefined") {
    return () => {};
  }

  setPerformerGmailOAuthReturnUrl();
  const authUrl = new URL(calendarAuthUrl);
  authUrl.searchParams.set("state", toPopupOauthState(PERFORMER_CALENDAR_OAUTH_STATE));
  window.open(authUrl.toString(), "_blank", "noopener");

  let stopped = false;
  let intervalId = null;
  const stop = () => {
    stopped = true;
    if (intervalId) {
      clearInterval(intervalId);
      intervalId = null;
    }
    document.removeEventListener("visibilitychange", handleVisibility);
  };

  const poll = async () => {
    if (stopped) return;
    try {
      const status = await getCalendarStatus();
      if (stopped) return;
      if (status?.connected === true || status?.calendar_authorized === true) {
        stop();
        onReady?.(status);
      }
    } catch {
      // keep polling until timeout
    }
  };
  const handleVisibility = () => {
    if (document.visibilityState === "visible") void poll();
  };

  void poll();
  intervalId = setInterval(poll, GMAIL_POLL_INTERVAL_MS);
  document.addEventListener("visibilitychange", handleVisibility);
  setTimeout(stop, GMAIL_POLL_TIMEOUT_MS);
  return stop;
}

/**
 * Open Attio OAuth in a popup and resolve when CRM write-back is connected.
 *
 * @example
 * // Input: onReady runs after OAuth succeeds
 * const stop = startPerformerAttioOAuthFlow(() => setCrmConnected(true));
 * // Output: Attio consent opens in a new tab; polling stops once connected
 *
 * @example
 * // Input: stop()
 * // Output: listeners and polling stop; onReady will not run again
 */
export function startPerformerAttioOAuthFlow(onReady, onError) {
  if (typeof window === "undefined") {
    return () => {};
  }

  let stopped = false;
  let statusPollId = null;
  let popupClosedPollId = null;
  let giveUpTimeoutId = null;
  let unsubscribeOauthResult = null;

  const cleanup = () => {
    if (statusPollId) {
      clearInterval(statusPollId);
      statusPollId = null;
    }
    if (popupClosedPollId) {
      clearInterval(popupClosedPollId);
      popupClosedPollId = null;
    }
    if (giveUpTimeoutId) {
      clearTimeout(giveUpTimeoutId);
      giveUpTimeoutId = null;
    }
    unsubscribeOauthResult?.();
    unsubscribeOauthResult = null;
  };

  const stop = (succeeded = false) => {
    if (stopped) return;
    stopped = true;
    cleanup();
    if (!succeeded) {
      onError?.();
    }
  };

  const finish = () => {
    stop(true);
    onReady?.();
  };

  const consumeAttioPopupResult = (raw) => {
    if (!raw) return false;
    let result;
    try {
      result = JSON.parse(raw);
    } catch {
      return false;
    }
    if (result.provider !== "attio") {
      return false;
    }
    window.localStorage.removeItem(BUSINESS_OAUTH_POPUP_RESULT_KEY);
    if (result.error) {
      stop(false);
      onError?.(result.error);
      return true;
    }
    if (result.credentialId) {
      finish();
      return true;
    }
    return false;
  };

  const pollStatus = async () => {
    if (stopped) return;
    try {
      const status = await getAttioStatus();
      if (stopped) return;
      if (status?.connected === true) {
        finish();
      }
    } catch {
      // keep polling until timeout
    }
  };

  const start = async () => {
    try {
      unsubscribeOauthResult = subscribeBusinessOAuthPopupResult(
        "attio",
        (result) => {
          consumeAttioPopupResult(JSON.stringify(result));
        }
      );
      const authUrl = await beginAttioConnect({
        oauthState: toPopupOauthState(PERFORMER_ATTIO_OAUTH_STATE),
        redirect: false,
      });
      if (stopped) return;

      const popup = window.open(authUrl, "_blank");
      popupClosedPollId = window.setInterval(() => {
        if (!popup || !popup.closed) {
          return;
        }
        clearInterval(popupClosedPollId);
        popupClosedPollId = null;
        consumeAttioPopupResult(
          window.localStorage.getItem(BUSINESS_OAUTH_POPUP_RESULT_KEY)
        );
      }, 500);

      void pollStatus();
      statusPollId = setInterval(pollStatus, GMAIL_POLL_INTERVAL_MS);
      giveUpTimeoutId = setTimeout(() => stop(false), GMAIL_POLL_TIMEOUT_MS);
    } catch (err) {
      stop(false);
      onError?.(err?.message || "Could not start Attio connection");
    }
  };

  void start();
  return () => stop(false);
}

/**
 * Open Salesforce OAuth in a popup and resolve when CRM write-back is connected.
 *
 * Mirrors ``startPerformerAttioOAuthFlow`` exactly, swapped to the Salesforce
 * status/connect endpoints and popup-result provider tag.
 *
 * @example
 * // Input: onReady runs after OAuth succeeds
 * const stop = startPerformerSalesforceOAuthFlow(() => setCrmConnected(true));
 * // Output: Salesforce consent opens in a new tab; polling stops once connected
 *
 * @example
 * // Input: stop()
 * // Output: listeners and polling stop; onReady will not run again
 */
export function startPerformerSalesforceOAuthFlow(onReady, onError) {
  if (typeof window === "undefined") {
    return () => {};
  }

  let stopped = false;
  let statusPollId = null;
  let popupClosedPollId = null;
  let giveUpTimeoutId = null;
  let unsubscribeOauthResult = null;

  const cleanup = () => {
    if (statusPollId) {
      clearInterval(statusPollId);
      statusPollId = null;
    }
    if (popupClosedPollId) {
      clearInterval(popupClosedPollId);
      popupClosedPollId = null;
    }
    if (giveUpTimeoutId) {
      clearTimeout(giveUpTimeoutId);
      giveUpTimeoutId = null;
    }
    unsubscribeOauthResult?.();
    unsubscribeOauthResult = null;
  };

  const stop = (succeeded = false) => {
    if (stopped) return;
    stopped = true;
    cleanup();
    if (!succeeded) {
      onError?.();
    }
  };

  const finish = () => {
    stop(true);
    onReady?.();
  };

  const consumeSalesforcePopupResult = (raw) => {
    if (!raw) return false;
    let result;
    try {
      result = JSON.parse(raw);
    } catch {
      return false;
    }
    if (result.provider !== "salesforce") {
      return false;
    }
    window.localStorage.removeItem(BUSINESS_OAUTH_POPUP_RESULT_KEY);
    if (result.error) {
      stop(false);
      onError?.(result.error);
      return true;
    }
    if (result.credentialId) {
      finish();
      return true;
    }
    return false;
  };

  const pollStatus = async () => {
    if (stopped) return;
    try {
      const status = await getSalesforceStatus();
      if (stopped) return;
      if (status?.connected === true) {
        finish();
      }
    } catch {
      // keep polling until timeout
    }
  };

  const start = async () => {
    try {
      unsubscribeOauthResult = subscribeBusinessOAuthPopupResult(
        "salesforce",
        (result) => {
          consumeSalesforcePopupResult(JSON.stringify(result));
        }
      );
      const authUrl = await beginSalesforceConnect({
        oauthState: toPopupOauthState(PERFORMER_SALESFORCE_OAUTH_STATE),
        redirect: false,
      });
      if (stopped) return;

      const popup = window.open(authUrl, "_blank");
      popupClosedPollId = window.setInterval(() => {
        if (!popup || !popup.closed) {
          return;
        }
        clearInterval(popupClosedPollId);
        popupClosedPollId = null;
        consumeSalesforcePopupResult(
          window.localStorage.getItem(BUSINESS_OAUTH_POPUP_RESULT_KEY)
        );
      }, 500);

      void pollStatus();
      statusPollId = setInterval(pollStatus, GMAIL_POLL_INTERVAL_MS);
      giveUpTimeoutId = setTimeout(() => stop(false), GMAIL_POLL_TIMEOUT_MS);
    } catch (err) {
      stop(false);
      onError?.(err?.message || "Could not start Salesforce connection");
    }
  };

  void start();
  return () => stop(false);
}
